import type { ConfiguratorPreviewContext } from '../preview-context';
import type { V3ComponentResponse } from './types';
import { resolveToken, TokenResolutionError } from './resolveToken';

export class ComponentFetchError extends Error {
    constructor(
        public readonly status: number,
        public readonly bodySnippet: string,
    ) {
        super(`Component fetch failed (HTTP ${status})`);
        this.name = 'ComponentFetchError';
    }
}

/**
 * Fetch a Component from the V3 cloud endpoint using a preview token. The
 * token is short-lived (15min, single-Component scope — see
 * `ComponentController::v3PreviewParams`). The bundle is meant to call this
 * once per mount; cache / retry strategy belongs to the caller, not here.
 *
 * Two ways the caller can supply the token:
 *  - `ctx.token` set directly (admin-iframe path: minted server-side at
 *    render time and embedded in the HTML).
 *  - `ctx.tokenEndpoint` set (storefront-plugin path: server-side proxy
 *    URL that returns `{token, expires_in}` JSON; the plugin keeps the
 *    merchant's secure_key off the wire). When both are set, the endpoint
 *    wins so plugin embeds can defensively leave a stale `token` in place.
 */
export async function fetchComponent(
    ctx: ConfiguratorPreviewContext,
): Promise<V3ComponentResponse> {
    const url = `https://${ctx.cloudBaseUrl}/api/v3/components/${ctx.componentId}`;
    // Single attempt with an embedded token; for plugin-storefront the
    // tokenEndpoint mints a fresh one each call, so a 401 here means the
    // CACHED preview-token at the cloud-frontend was wiped (Coolify deploy,
    // Redis restart, TTL elapsed). Retry once with a *new* token from
    // the endpoint — that's the only legitimate self-heal path. When the
    // first attempt already used a newly-minted token (no embedded), a
    // retry would be wasted, so we gate on canForceMint.
    const canForceMint = Boolean(ctx.tokenEndpoint && ctx.tokenEndpoint.length > 0);
    let token: string;
    try {
        token = await resolveToken(ctx);
    } catch (err) {
        if (err instanceof TokenResolutionError) {
            throw new ComponentFetchError(err.status, `tokenEndpoint: ${err.bodySnippet}`);
        }
        throw err;
    }

    let resp = await fetch(url, { headers: { 'x-jscloud-tok': token } });

    if (resp.status === 401 && canForceMint) {
        // Mint a fresh token via `force=1` so the plugin bypasses its
        // 8-min mint cache. Without force the plugin would just return
        // the same dead token we already tried — its cache outlives the
        // cloud-side cache that got wiped (Coolify deploy, Redis
        // restart, TTL elapsed). The retry always uses a brand-new
        // admin-minted token.
        try {
            const fresh = await resolveToken(ctx, { forceRefresh: true });
            if (fresh) {
                resp = await fetch(url, { headers: { 'x-jscloud-tok': fresh } });
            }
        } catch (err) {
            if (err instanceof TokenResolutionError) {
                throw new ComponentFetchError(err.status, `tokenEndpoint: ${err.bodySnippet}`);
            }
            throw err;
        }
    }

    if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new ComponentFetchError(resp.status, body.slice(0, 200));
    }
    return (await resp.json()) as V3ComponentResponse;
}
